import {createAction} from '@reduxjs/toolkit';
import {createApi, fetchBaseQuery} from '@reduxjs/toolkit/query/react';
import {isApiResponseBodyError, isApiResponseBodySuccess} from '../utils/apiUtils';
import {getConfigValue} from '../utils/configUtils';

const baseQuery = fetchBaseQuery({
  baseUrl:
    import.meta.env.VITE_NGROK_ENABLED === 'true'
      ? `${import.meta.env.VITE_NGROK_URL}/api`
      : `${getConfigValue('apiUrl')}`,
  credentials: import.meta.env.VITE_NGROK_ENABLED === 'true' ? undefined : 'include',
  prepareHeaders: (headers, {getState}) => {
    const state = getState() as {auth: {token?: string | null}};
    const token = state.auth.token;
    if (import.meta.env.VITE_NGROK_ENABLED === 'true') {
      headers.set('ngrok-skip-browser-warning', 'true');
    }
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

let refreshPromise: any = null;

const baseQueryWithRefreshToken = async (args: any, apiArg: any, extraOptions: any) => {
  let result = await baseQuery(args, apiArg, extraOptions);

  if (result?.error?.status === 401 || result?.error?.status === 403) {
    // send refresh token to get new access token
    const refreshOwner = refreshPromise == null;
    refreshPromise = refreshPromise ?? baseQuery({url: '/identities/refresh', method: 'POST'}, apiArg, extraOptions);
    const refreshResult = await refreshPromise;
    if (refreshOwner) {
      refreshPromise = null;
      if (refreshResult?.data) {
        // store the new token
        apiArg.dispatch(refreshTokenFulfilled(refreshResult.data as {token: string}));
        // retry the original query with new access token
        result = await baseQuery(args, apiArg, extraOptions);
      } else {
        apiArg.dispatch(refreshTokenRejected(''));
        apiArg.dispatch(api.util.resetApiState());
      }
    } else {
      if (refreshResult?.data) {
        result = await baseQuery(args, apiArg, extraOptions);
      }
    }
  }

  if (isApiResponseBodySuccess(result?.data)) {
    result.data = result.data.data;
  }

  if (isApiResponseBodyError(result?.data)) {
    // @ts-expect-error redux-toolkit new types are not working TODO fix this
    result.error = {
      data: result.data,
      error: result.data.message,
      status: 'CUSTOM_ERROR',
    };
    result.data = null;
  }

  if (result?.error?.status === 'FETCH_ERROR') {
    result.error.error = 'Network error. Please check your internet connection.';
  }

  if (result?.error?.status === 429) {
    result.error = {
      data: {
        message: 'Too many requests. Please try again later.',
        status: 'error',
        statusCode: '9999',
      },
      error: 'Too many requests. Please try again later.',
      status: 'CUSTOM_ERROR',
    };
    result.data = null;
  }

  return result;
};

export const api = createApi({
  baseQuery: baseQueryWithRefreshToken,
  endpoints: () => ({}),
  reducerPath: 'api',
  tagTypes: [
    'Avatar',
    'Biobook',
    'BiobookChapters',
    'BiobookCounts',
    'BiobookFavorites',
    'BiobookPreferences',
    'BiobookUpdates',
    'Biotwins',
    'BiotwinConditions',
    'BiotwinScores',
    'BiotwinSettings',
    'BiotwinShares',
    'IdentitySettings',
    'Invoices',
    'RelevantConditions',
    'TenantInvitations',
    'TenantUsers',
  ],
});

export const refreshTokenFulfilled = createAction('api/refreshTokenFulfilled', (payload: {token: string}) => ({
  payload,
}));

export const refreshTokenRejected = createAction('api/refreshTokenRejected', (error: unknown) => ({payload: {error}}));
